import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button/Button';
import { SearchIcon } from '@/components/ui/Icons';
import { NavbarSearchControl } from '@/components/Layout/NavbarSearchControl';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import '@/components/ProjectsTable/EditableCell.css';
import './NavbarSearchPopoverControl.css';

interface NavbarSearchPopoverControlProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder: string;
  ariaLabel: string;
  dialogTitle: string;
}

export function NavbarSearchPopoverControl({
  value,
  onChange,
  onClear,
  placeholder,
  ariaLabel,
  dialogTitle,
}: NavbarSearchPopoverControlProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const hasValue = value.trim().length > 0;

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    open,
    useCallback(() => {
      handleClose();
    }, [handleClose]),
    true
  );

  usePopoverPositioning({
    triggerRef,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    horizontalAlign: 'end',
    offset: 8,
    zIndex: 1200,
    observeTriggerResize: false,
    repositionOnScroll: false,
  });

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    enabled: shouldRender,
    capture: false,
    excludeRefs: [triggerRef],
  });

  useEffect(() => {
    if (!shouldRender) return;
    const timerId = window.setTimeout(() => {
      popoverRef.current?.querySelector('input')?.focus();
    }, 0);
    return () => window.clearTimeout(timerId);
  }, [shouldRender]);

  const popoverAnchorStyle: CSSProperties = {
    position: 'fixed',
    pointerEvents: 'auto',
    left: triggerRef.current ? -9999 : 8,
    top: triggerRef.current ? -9999 : 8,
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant={hasValue ? 'primary' : 'outlined'}
        size="s"
        className="navbar__header-icon-btn navbar-search-popover__trigger"
        onClick={() => setOpen((current) => !current)}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={hasValue ? `${dialogTitle}: есть текст поиска` : dialogTitle}
      >
        <SearchIcon size={18} aria-hidden />
      </Button>

      {shouldRender
        ? createPortal(
            <div ref={popoverRef} style={popoverAnchorStyle}>
              <OverlayIntrinsicSize>
                <div className={`editable-cell-popover navbar-search-popover ${contentClasses}`}>
                  <div className="editable-cell-popover__form navbar-search-popover__form">
                    <NavbarSearchControl
                      className="navbar-search-popover__field"
                      value={value}
                      onChange={onChange}
                      onClear={onClear}
                      placeholder={placeholder}
                      ariaLabel={ariaLabel}
                    />
                  </div>
                </div>
              </OverlayIntrinsicSize>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
